import axios from "axios";
import React from "react";
import styled from "styled-components";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";
import { ManualFixContent } from "./ManualFixContent";

const SERVICES_URL = process.env.SERVICES_URL;
const MANUAL_FIX_API_URL = `${SERVICES_URL}/manual-fix`;

const ModalBodyStyle = styled.div.attrs(() => ({
    className: "m-4",
}))``;

type ManualFixModalBodyProps = {
    personnelId: number;
    personnel: any;
};

const ManualFixModalBody: React.FC<ManualFixModalBodyProps> = ({ personnelId, personnel }) => {
    const { dispatch } = useGlobalModal();

    return (
        <ModalBodyStyle>
            <ManualFixContent
                personnelId={personnelId}
                personnel={personnel}
                isStandalone={true}
                onActionStateChange={actionState => {
                    // Update global modal state when actions change
                    if (actionState.consentFormAction) {
                        dispatch({
                            type: "updateConsentFormAction",
                            action: actionState.consentFormAction,
                        });
                    }
                    if (actionState.statusChangeAction) {
                        dispatch({
                            type: "updateStatusChangeAction",
                            action: actionState.statusChangeAction,
                        });
                    }
                    if (actionState.newStatus) {
                        dispatch({ type: "updateNewStatus", status: actionState.newStatus });
                    }
                    if (actionState.selectedResendConsentFormId) {
                        dispatch({
                            type: "updateSelectedResendConsentFormId",
                            consentFormId: actionState.selectedResendConsentFormId,
                        });
                    }
                    if (actionState.selectedCancelConsentFormId) {
                        dispatch({
                            type: "updateSelectedCancelConsentFormId",
                            consentFormId: actionState.selectedCancelConsentFormId,
                        });
                    }
                }}
            />
        </ModalBodyStyle>
    );
};

type ManualFixModalFooterProps = {
    personnelId: number;
    consentFormAction: string;
    backgroundCheckAction: string;
    backgroundCheckJurisdiction: string;
    statusChangeAction: string;
    newStatus: string;
    selectedResendConsentFormId: string;
    selectedCancelConsentFormId: string;
};

const ManualFixModalFooter: React.FC<ManualFixModalFooterProps> = ({
    personnelId,
    consentFormAction,
    backgroundCheckAction,
    backgroundCheckJurisdiction,
    statusChangeAction,
    newStatus,
    selectedResendConsentFormId,
    selectedCancelConsentFormId,
}) => {
    const { closeModal } = useGlobalModal();
    const showAlert = useAlert();

    const handleSubmit = async () => {
        try {
            // Handle consent form actions
            if (consentFormAction === "resend") {
                const url = selectedResendConsentFormId
                    ? `${MANUAL_FIX_API_URL}/consent-form/resend/${personnelId}?consentFormId=${selectedResendConsentFormId}`
                    : `${MANUAL_FIX_API_URL}/consent-form/resend/${personnelId}`;
                await axios.post(url);
            } else if (consentFormAction === "cancel") {
                const url = selectedCancelConsentFormId
                    ? `${MANUAL_FIX_API_URL}/consent-form/cancel/${personnelId}?consentFormId=${selectedCancelConsentFormId}`
                    : `${MANUAL_FIX_API_URL}/consent-form/cancel/${personnelId}`;
                await axios.post(url);
            }

            // Handle background check actions
            if (backgroundCheckAction === "resend") {
                await axios.post(
                    `${MANUAL_FIX_API_URL}/background-check/resend/${personnelId}?jurisdiction=${encodeURIComponent(
                        backgroundCheckJurisdiction,
                    )}`,
                );
            }

            // Handle status change action
            if (statusChangeAction === "change" && newStatus) {
                await axios.post(
                    `${MANUAL_FIX_API_URL}/status-change/${personnelId}?newStatus=${encodeURIComponent(
                        newStatus,
                    )}`,
                );
            }

            showAlert("Manual fix submitted successfully", "success");
            closeModal();
        } catch (error) {
            console.error("Error submitting manual fix:", error);
            showAlert("Error submitting manual fix. Please try again.", "error");
        }
    };

    const handleCancel = () => {
        closeModal();
    };

    return (
        <div className="d-flex justify-content-end gap-2">
            <OutlinedButtonStyled onClick={handleCancel} height={31} width={80}>
                Cancel
            </OutlinedButtonStyled>
            <PrimaryButtonStyled onClick={handleSubmit} height={31} width={80}>
                Submit
            </PrimaryButtonStyled>
        </div>
    );
};

ManualFixModalBody.displayName = "BodyContent";
ManualFixModalFooter.displayName = "FooterContent";

export { ManualFixModalBody, ManualFixModalFooter };
